import { useQuery } from "@tanstack/react-query";

import { getPlatformLocationCoordinates } from "@/api/methods";

import { findUnambiguousNearestLocation } from "./findUnambiguousNearestLocation";
import { requestGuestPosition } from "./requestGuestPosition";

export type GuestLocationSuggestion = {
  locationId: string;
  /**
   * `precise`: the browser places the visitor unambiguously at this branch (200 m rule).
   * `approximate`: the branch nearest the visitor's network location, used when there is no
   * precise answer (permission denied, unavailable, timed out, or ambiguous).
   */
  source: "precise" | "approximate";
};

export const useGuestLocationSuggestion = (locationIds: string[], enabled: boolean) =>
  useQuery({
    queryKey: ["guestLocationSuggestion", locationIds],
    queryFn: async (): Promise<GuestLocationSuggestion | null> => {
      const response = await getPlatformLocationCoordinates();
      const { locations } = response;
      if (
        locations.length !== locationIds.length ||
        locations.some(
          (location) =>
            !locationIds.includes(location.locationId) ||
            location.latitude === null ||
            location.longitude === null,
        )
      ) {
        return null;
      }

      // Keep the visitor's coordinates inside this calculation; only the suggested branch is cached.
      const position = await requestGuestPosition();
      const preciseLocationId =
        position === null ? null : findUnambiguousNearestLocation(locations, position);
      if (preciseLocationId) return { locationId: preciseLocationId, source: "precise" };

      const approximateLocationId = response.approximateNearestLocationId;
      return approximateLocationId && locationIds.includes(approximateLocationId)
        ? { locationId: approximateLocationId, source: "approximate" }
        : null;
    },
    enabled: enabled && locationIds.length > 1,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: 0,
    throwOnError: false,
  });
